import React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Controller, type UseFormReturn, useWatch } from "react-hook-form";
import {
  DOCUMENT_TYPE_LABELS,
  REGISTRY_ADJACENT_KIND_LABELS,
  REQUEST_TYPE_LABELS,
} from "../constants/priceMaster";
import {
  type AreaCode,
  type DocumentType,
  type RegistryAdjacentKind,
  type ResearchAccess,
  type ResearchRequestFormData,
  type RequestType,
  type SpecialOptions,
} from "../types";
import { isResearchType } from "../utils/calculateEstimate";
import { type DeliverySchedule } from "../utils/deliverySchedule";
import { AccessSurchargeSection } from "./AccessSurchargeSection";
import { CLabeledCheckbox } from "./CLabeledCheckbox";
import { DeliverySection } from "./DeliverySection";
import { PropertyInputSection } from "./PropertyInputSection";
import {
  FieldLabel,
  HelpText,
  HelpTip,
  inputSx,
  optionBoxSx,
  SectionCard,
} from "./formLayout";

interface ResearchDetailsStepProps {
  form: UseFormReturn<ResearchRequestFormData>;
  detectedArea: AreaCode | null;
  standardDelivery: string | null;
  delivery: DeliverySchedule | null;
  /** 最寄駅による僻地加算・車両必須加算の判定 */
  access: ResearchAccess;
  /** 今の地番・対象住所での判定を問い合わせ中 */
  accessLoading: boolean;
  /** 入力の済んだ地番がある */
  hasChiban: boolean;
}

const documentTypes = Object.keys(DOCUMENT_TYPE_LABELS) as DocumentType[];
const requestTypeOptions = Object.keys(REQUEST_TYPE_LABELS) as RequestType[];
const registryAdjacentKinds = Object.keys(
  REGISTRY_ADJACENT_KIND_LABELS
) as RegistryAdjacentKind[];

/** 選んだ順に左右されないよう、一覧の並び順で返す（同じ依頼を同じ再送キーにするため） */
const toggleRequestType = (
  selected: RequestType[],
  requestType: RequestType,
  checked: boolean
): RequestType[] =>
  requestTypeOptions.filter((type) =>
    type === requestType ? checked : selected.includes(type)
  );

/** 物件の規模の欄。しきい値と加算率は価格表（priceMaster・calculateEstimate）と同じ */
const propertySizeFields: Array<{
  name: keyof SpecialOptions;
  label: string;
  unit: string;
  example: string;
  tip: string;
  integer: boolean;
}> = [
  {
    name: "siteAreaSqm",
    label: "敷地面積",
    unit: "㎡",
    example: "600",
    tip: "500㎡以上 +10%、1,000㎡以上 +20%、3,000㎡以上 +30%、5,000㎡以上 +50%",
    integer: false,
  },
  {
    name: "subdivisionUnits",
    label: "宅地戸建分譲の戸数",
    unit: "戸",
    example: "5",
    tip: "3戸以上 +10%、5戸以上 +20%、7戸以上 +30%",
    integer: true,
  },
  {
    name: "condominiumUnits",
    label: "新築マンションの戸数",
    unit: "戸",
    example: "30",
    tip: "20戸以下 +10%、21戸以上 +20%、51戸以上 +30%、101戸以上 +50%、201戸以上 +80%",
    integer: true,
  },
  {
    name: "registeredLots",
    label: "登記の筆数",
    unit: "筆",
    example: "3",
    tip: "5筆以上 +10%、10筆以上 +20%、15筆以上 +30%",
    integer: true,
  },
];

// 写真撮影の加算は簡易調査の基本価格にだけ付き、詳細調査には付かない（calculateEstimate と同じ）
const onsitePhotoHelpText = (requestTypes: RequestType[]): string => {
  const hasSimple = requestTypes.includes("simple");
  const hasDetailed = requestTypes.includes("detailed");
  if (hasSimple && hasDetailed) {
    return "簡易調査に写真撮影あり価格を適用します（簡易調査の基本価格 +5,000円）。詳細調査は加算なしです。";
  }
  if (hasDetailed) {
    return "詳細調査では現地調査を選択しても金額加算はありません。";
  }
  return "写真撮影あり価格を適用（基本価格 +5,000円）";
};

const numericInputOptions = {
  setValueAs: (value: string): number | undefined =>
    value === "" ? undefined : Number(value),
};

export const ResearchDetailsStep: React.FC<ResearchDetailsStepProps> = ({
  form,
  detectedArea,
  standardDelivery,
  delivery,
  access,
  accessLoading,
  hasChiban,
}) => {
  const { control, register, setValue, formState } = form;
  const requestTypes = useWatch({ control, name: "requestTypes" });
  const selectedDocuments = useWatch({ control, name: "selectedDocuments" });
  const registryAdjacent = useWatch({ control, name: "registryAdjacent" });
  const isResearchRequest = requestTypes.some(isResearchType);

  const resetResearchOnlyOptions = (): void => {
    setValue("onsitePhoto", false);
    setValue("specialOptions", {});
  };

  return (
    <Stack spacing={2}>
      <PropertyInputSection form={form} />

      <SectionCard
        title="不動産所有者名"
        description="所有者が判明している場合記入ください。不明の場合は記載不要です。"
      >
        <TextField
          {...register("ownerName")}
          placeholder="不動産所有者名"
          size="small"
          fullWidth
          inputProps={{ "aria-label": "不動産所有者名" }}
          error={Boolean(formState.errors.ownerName)}
          helperText={formState.errors.ownerName?.message}
          sx={inputSx}
        />
      </SectionCard>

      <Box
        component="section"
        sx={{
          p: { xs: 2, sm: 2.5 },
          backgroundColor: "#f8f9fa",
          borderRadius: "16px",
          border: "1px solid #e9ecef",
        }}
      >
        <Typography
          id="request-type-heading"
          variant="subtitle2"
          component="h3"
          sx={{
            fontSize: { xs: "13px", sm: "14px" },
            fontWeight: 700,
            letterSpacing: "1px",
            mb: 1.5,
          }}
        >
          依頼種別（必須）
        </Typography>
        <FormControl
          component="fieldset"
          error={Boolean(formState.errors.requestTypes)}
          required
          fullWidth
        >
          <Controller
            control={control}
            name="requestTypes"
            render={({ field }) => (
              <Box
                role="group"
                aria-labelledby="request-type-heading"
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 1.5,
                }}
              >
                {requestTypeOptions.map((type) => {
                  const checked = field.value.includes(type);
                  return (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={(event) => {
                            const next = toggleRequestType(
                              field.value,
                              type,
                              event.target.checked
                            );
                            field.onChange(next);
                            if (!next.some(isResearchType)) {
                              resetResearchOnlyOptions();
                            }
                          }}
                          onBlur={field.onBlur}
                        />
                      }
                      label={REQUEST_TYPE_LABELS[type]}
                      sx={optionBoxSx(checked)}
                    />
                  );
                })}
              </Box>
            )}
          />
          <FormHelperText>
            {formState.errors.requestTypes?.message}
          </FormHelperText>
        </FormControl>

        {isResearchRequest ? (
          <Box sx={{ mt: 1.5 }}>
            <CLabeledCheckbox
              control={control}
              name="onsitePhoto"
              label="現地調査（写真撮影）"
            />
            <HelpText>{onsitePhotoHelpText(requestTypes)}</HelpText>
          </Box>
        ) : null}
      </Box>

      {isResearchRequest ? (
        <SectionCard
          title="物件の規模"
          titleTip="大きな物件は、規模に応じて基本価格に加算します。当てはまるものだけ入力してください。複数当てはまるときは、最も高い率を使います。"
        >
          <Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                gap: 1.5,
              }}
            >
              {propertySizeFields.map(
                ({ name, label, unit, example, tip, integer }) => {
                  const error = formState.errors.specialOptions?.[name];
                  return (
                    <Box key={name}>
                      {/* 項目名の下の余白を外側に移し、ⓘと項目名の上下の中央をそろえる */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 0.75,
                          "& .MuiTypography-root": { mb: 0 },
                          "& .MuiIconButton-root": { my: -0.5 },
                        }}
                      >
                        <FieldLabel htmlFor={`property-size-${name}`}>
                          {label}
                        </FieldLabel>
                        <HelpTip title={tip} label={label} />
                      </Box>
                      <TextField
                        {...register(
                          `specialOptions.${name}`,
                          numericInputOptions
                        )}
                        id={`property-size-${name}`}
                        placeholder={`例：${example}`}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {unit}
                            </InputAdornment>
                          ),
                        }}
                        type="number"
                        size="small"
                        fullWidth
                        inputProps={{
                          "aria-label": label,
                          ...(integer
                            ? { inputMode: "numeric", pattern: "\\d*" }
                            : {}),
                        }}
                        error={Boolean(error)}
                        helperText={error?.message}
                        sx={inputSx}
                      />
                    </Box>
                  );
                }
              )}
            </Box>
          </Box>
        </SectionCard>
      ) : null}

      {isResearchRequest ? (
        <AccessSurchargeSection
          access={access}
          loading={accessLoading}
          hasChiban={hasChiban}
        />
      ) : null}

      <SectionCard
        title="貴社から弊社に取得を依頼する資料"
        titleTip="取得にかかる実費（法務局などの手数料）は、調査費用に加えてご請求します。"
      >
        <Controller
          control={control}
          name="selectedDocuments"
          render={({ field }) => (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 1.5,
              }}
            >
              {documentTypes.map((documentType) => {
                const checked = field.value.includes(documentType);
                const option = (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={(event) => {
                          field.onChange(
                            event.target.checked
                              ? [...field.value, documentType]
                              : field.value.filter(
                                  (selected) => selected !== documentType
                                )
                          );
                        }}
                      />
                    }
                    label={DOCUMENT_TYPE_LABELS[documentType]}
                    sx={optionBoxSx(checked)}
                  />
                );
                // 説明のアイコンはラベルの外に置き、押してもチェックが切り替わらないようにする
                return documentType === "registry" ? (
                  <Box
                    key={documentType}
                    sx={{ position: "relative", display: "grid" }}
                  >
                    {option}
                    <Box
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      <HelpTip
                        title="信託目録と共同担保目録を付けて取得します。"
                        label={DOCUMENT_TYPE_LABELS[documentType]}
                      />
                    </Box>
                  </Box>
                ) : (
                  <React.Fragment key={documentType}>{option}</React.Fragment>
                );
              })}
            </Box>
          )}
        />
        {selectedDocuments.includes("registry") ? (
          <Box
            sx={{
              mt: 2,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: 1.5,
            }}
          >
            <FormControl component="fieldset">
              <FieldLabel>登記簿謄本の範囲</FieldLabel>
              <Controller
                control={control}
                name="registryAdjacent"
                render={({ field }) => (
                  <RadioGroup
                    row
                    aria-label="登記簿謄本の範囲"
                    value={field.value ? "with_adjacent" : "target_only"}
                    onChange={(event) => {
                      const withAdjacent =
                        event.target.value === "with_adjacent";
                      field.onChange(withAdjacent);
                      if (!withAdjacent) setValue("registryAdjacentKind", "");
                    }}
                  >
                    <FormControlLabel
                      value="target_only"
                      control={<Radio size="small" />}
                      label="対象地のみ"
                    />
                    <FormControlLabel
                      value="with_adjacent"
                      control={<Radio size="small" />}
                      label="隣接地も含む"
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
            {registryAdjacent ? (
              <FormControl
                component="fieldset"
                error={Boolean(formState.errors.registryAdjacentKind)}
              >
                <FieldLabel required>隣接地の種類</FieldLabel>
                <Controller
                  control={control}
                  name="registryAdjacentKind"
                  render={({ field }) => (
                    <RadioGroup
                      row
                      aria-label="隣接地の種類"
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    >
                      {registryAdjacentKinds.map((kind) => (
                        <FormControlLabel
                          key={kind}
                          value={kind}
                          control={<Radio size="small" />}
                          label={REGISTRY_ADJACENT_KIND_LABELS[kind]}
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
                <FormHelperText>
                  {formState.errors.registryAdjacentKind?.message}
                </FormHelperText>
              </FormControl>
            ) : null}
          </Box>
        ) : null}
        <Controller
          control={control}
          name="customDocuments"
          render={({ field }) => (
            <Box sx={{ mt: 2 }}>
              <FieldLabel>その他の取得資料</FieldLabel>
              <Stack spacing={1}>
                {field.value.map((document, index) => (
                  <Box
                    key={index}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <TextField
                      value={document}
                      placeholder={`その他の取得資料 ${index + 1}`}
                      onChange={(event) => {
                        const nextDocuments = [...field.value];
                        nextDocuments[index] = event.target.value;
                        field.onChange(nextDocuments);
                      }}
                      inputProps={{
                        "aria-label": `その他の取得資料 ${index + 1}`,
                      }}
                      fullWidth
                      size="small"
                      sx={inputSx}
                    />
                    {field.value.length > 1 ? (
                      <Button
                        color="error"
                        aria-label={`その他の取得資料 ${index + 1}を削除`}
                        onClick={() => {
                          field.onChange(
                            field.value.filter(
                              (_, itemIndex) => itemIndex !== index
                            )
                          );
                        }}
                        sx={{ flexShrink: 0 }}
                      >
                        削除
                      </Button>
                    ) : null}
                  </Box>
                ))}
              </Stack>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  field.onChange([...field.value, ""]);
                }}
                sx={{
                  mt: 1.5,
                  borderRadius: 3,
                  borderStyle: "dashed",
                  borderWidth: 2,
                  "&:hover": { borderStyle: "solid", borderWidth: 2 },
                }}
              >
                入力欄を追加
              </Button>
              {formState.errors.customDocuments?.message ? (
                <FormHelperText error>
                  {formState.errors.customDocuments.message}
                </FormHelperText>
              ) : null}
            </Box>
          )}
        />
      </SectionCard>

      <DeliverySection
        form={form}
        detectedArea={detectedArea}
        standardDelivery={standardDelivery}
        delivery={delivery}
      />
    </Stack>
  );
};
